import {
  mdiAlertCircleOutline,
  mdiAlertOutline,
  mdiCancel,
  mdiCheck,
  mdiClockOutline,
  mdiFlash,
  mdiHospitalBuilding,
  mdiPackageVariantClosed,
  mdiReceiptTextOutline,
  mdiTimerOffOutline,
  mdiTimerSandComplete,
  mdiViewDashboardOutline
} from '@mdi/js'

import type { QueueTone } from '@/components/designSystem/QueueViews/types'
import type {
  AffiliateSlaState,
  FunnelStage,
  PackageStage,
  PackagesTab,
  PendingSlaState
} from '@/Network/Types/Responses/AntecipeAffiliateResponses'

/** Rótulos pt-BR ficam no web (contrato B, "Enums compartilhados"). */

export type AffiliatePage = 'overview' | 'packages' | 'payout' | 'units'

export const AFFILIATE_PAGES: AffiliatePage[] = ['overview', 'packages', 'payout', 'units']

export const PACKAGE_STAGE_ORDER: FunnelStage[] = [
  'COMPANY_REVIEW',
  'ANTECIPE_REVIEW',
  'DOCTOR_SIGNATURE',
  'AWAITING_PAYMENT',
  'PAID'
]

export const PACKAGE_STAGE_LABELS: Record<PackageStage, string> = {
  COMPANY_REVIEW: 'Conferência da empresa',
  ANTECIPE_REVIEW: 'Análise Antecipe',
  DOCTOR_SIGNATURE: 'Assinatura do médico',
  AWAITING_PAYMENT: 'Aguardando pagamento',
  PAID: 'Pago',
  CANCELED: 'Cancelado',
  EXPIRED: 'Expirado'
}

export const PACKAGE_STAGE_DESCRIPTIONS: Record<FunnelStage, string> = {
  COMPANY_REVIEW: 'A empresa médica confere os plantões do pacote',
  ANTECIPE_REVIEW: 'Antecipe valida o pacote e gera o contrato',
  DOCTOR_SIGNATURE: 'Contrato enviado, aguardando assinatura',
  AWAITING_PAYMENT: 'Aprovado, pagamento em processamento',
  PAID: 'pacotes pagos nesta competência'
}

export const AFFILIATE_SLA_LABELS: Record<AffiliateSlaState, string> = {
  OVERDUE: 'Estourado',
  URGENT: 'Urgente',
  ATTENTION: 'Atenção',
  ON_TIME: 'No prazo',
  DONE: 'Pago no prazo',
  DONE_LATE: 'Pago fora do prazo',
  CANCELED: 'Cancelado',
  EXPIRED: 'Expirado'
}

/** Mesmas cores do PrioritySlaStateBadge + os dois estados que só o portal tem. */
export const AFFILIATE_SLA_PILL_CLASS: Record<AffiliateSlaState, string> = {
  OVERDUE: 'bg-red-700 text-white',
  URGENT: 'bg-orange-100 text-orange-800',
  ATTENTION: 'bg-amber-100 text-amber-800',
  ON_TIME: 'bg-blue-50 text-blue-700',
  DONE: 'bg-green-50 text-green-700',
  DONE_LATE: 'bg-orange-50 text-orange-700',
  CANCELED: 'bg-lightGray30 text-neutral50',
  EXPIRED: 'bg-lightGray30 text-neutral50'
}

export const AFFILIATE_SLA_TEXT_CLASS: Record<AffiliateSlaState, string> = {
  OVERDUE: 'text-red-700',
  URGENT: 'text-orange-700',
  ATTENTION: 'text-amber-700',
  ON_TIME: 'text-textPrimary',
  DONE: 'text-green-700',
  DONE_LATE: 'text-orange-700',
  CANCELED: 'text-neutral50',
  EXPIRED: 'text-neutral50'
}

/** Ícone + cor: o estado não depende só da cor (daltonismo). */
export const AFFILIATE_SLA_ICON: Record<AffiliateSlaState, string> = {
  OVERDUE: mdiAlertCircleOutline,
  URGENT: mdiFlash,
  ATTENTION: mdiAlertOutline,
  ON_TIME: mdiClockOutline,
  DONE: mdiCheck,
  DONE_LATE: mdiTimerSandComplete,
  CANCELED: mdiCancel,
  EXPIRED: mdiTimerOffOutline
}

export const PENDING_SLA_STATES: PendingSlaState[] = ['OVERDUE', 'URGENT', 'ATTENTION', 'ON_TIME']

export const isPendingSlaState = (state: AffiliateSlaState): state is PendingSlaState =>
  (PENDING_SLA_STATES as AffiliateSlaState[]).includes(state)

export const stageTone = (stage: PackageStage): QueueTone =>
  stage === 'PAID' ? 'ok' : stage === 'CANCELED' || stage === 'EXPIRED' ? 'neutral' : 'info'

export type StageStep = 'done' | 'current' | 'todo' | 'canceled'

export const stageSteps = (stage: PackageStage): StageStep[] => {
  if (stage === 'CANCELED') return PACKAGE_STAGE_ORDER.map(() => 'canceled')
  // Expirado: parou na conferência da empresa (único lugar onde expira).
  if (stage === 'EXPIRED')
    return PACKAGE_STAGE_ORDER.map((_, step) => (step === 0 ? 'canceled' : 'todo'))
  if (stage === 'PAID') return PACKAGE_STAGE_ORDER.map(() => 'done')
  const index = PACKAGE_STAGE_ORDER.indexOf(stage)
  return PACKAGE_STAGE_ORDER.map((_, step) =>
    step < index ? 'done' : step === index ? 'current' : 'todo'
  )
}

export const nextStage = (stage: PackageStage): FunnelStage | null => {
  if (stage === 'PAID' || stage === 'CANCELED' || stage === 'EXPIRED') return null
  return PACKAGE_STAGE_ORDER[PACKAGE_STAGE_ORDER.indexOf(stage) + 1] ?? null
}

export const PACKAGES_TAB_ORDER: PackagesTab[] = ['open', 'paid', 'canceled', 'expired', 'all']

export const PACKAGES_TAB_LABELS: Record<PackagesTab, string> = {
  open: 'Em andamento',
  paid: 'Pagos',
  canceled: 'Cancelados',
  expired: 'Expirados',
  all: 'Todos'
}

interface PageTitle {
  title: string
  subtitle: (ctx: { name: string; slaHours: number }) => string
}

export const AFFILIATE_PAGE_TITLES: Record<AffiliatePage, PageTitle> = {
  overview: {
    title: 'Visão geral',
    subtitle: ({ name }) =>
      `Antecipações de plantões nas unidades vinculadas a ${name}, contadas a partir do início de cada vínculo.`
  },
  packages: {
    title: 'Pacotes',
    subtitle: ({ slaHours }) =>
      `Cada pacote reúne os plantões de um médico enviados para antecipação. Acompanhe o prazo de ${slaHours} h e as etapas até o pagamento.`
  },
  payout: {
    title: 'Repasse',
    subtitle: ({ name }) =>
      `Quanto ${name} recebe sobre o bruto antecipado dos pacotes pagos, por competência.`
  },
  units: {
    title: 'Unidades',
    subtitle: ({ name }) => `Unidades de saúde cujos plantões contam para ${name}.`
  }
}

export const AFFILIATE_NAV: { page: AffiliatePage; label: string; icon: string }[] = [
  { page: 'overview', label: 'Visão geral', icon: mdiViewDashboardOutline },
  { page: 'packages', label: 'Pacotes', icon: mdiPackageVariantClosed },
  { page: 'payout', label: 'Repasse', icon: mdiReceiptTextOutline },
  { page: 'units', label: 'Unidades', icon: mdiHospitalBuilding }
]
